import { useCallback, useEffect, useRef, useState } from "react";
import { rcSpeak } from "../../api/ProcurementClient";

type SpeechRecognitionResultLike = {
  readonly isFinal: boolean;
  readonly 0: { transcript: string };
};

type SpeechRecognitionEventLike = {
  readonly resultIndex: number;
  readonly results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isSpeechRecognitionSupported(): boolean {
  return getSpeechRecognitionCtor() != null;
}

export function useSpeechRecognition(opts: {
  onFinalTranscript: (text: string) => void;
  lang?: string;
}) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  /** User wants mic on until they toggle off — restart after browser pauses. */
  const wantListeningRef = useRef(false);
  /** True while RC is speaking so we don't hear ourselves. */
  const ttsMutedRef = useRef(false);
  /** Ignore finals for a short window after TTS (trailing echo). */
  const ignoreUntilRef = useRef(0);
  const onFinalRef = useRef(opts.onFinalTranscript);
  onFinalRef.current = opts.onFinalTranscript;
  const langRef = useRef(opts.lang ?? "en-US");
  langRef.current = opts.lang ?? "en-US";
  const beginRecognitionRef = useRef<() => void>(() => {});

  const abortRecognitionSession = useCallback(() => {
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (!rec) return;
    try {
      rec.onend = null;
      rec.onerror = null;
      rec.onresult = null;
      rec.abort();
    } catch {
      /* ignore */
    }
  }, []);

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    ttsMutedRef.current = false;
    abortRecognitionSession();
    setListening(false);
    setInterim("");
  }, [abortRecognitionSession]);

  const beginRecognition = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor || !wantListeningRef.current || ttsMutedRef.current) return;

    abortRecognitionSession();

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = langRef.current;

    recognition.onresult = (event) => {
      if (ttsMutedRef.current || Date.now() < ignoreUntilRef.current) {
        return;
      }
      let interimText = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }
      setInterim(interimText);
      if (finalText.trim()) {
        setInterim("");
        onFinalRef.current(finalText.trim());
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "aborted") {
        return;
      }
      if (event.error === "no-speech" || event.error === "network") {
        return;
      }
      if (event.error === "not-allowed") {
        wantListeningRef.current = false;
        setError(
          "Microphone permission denied. Allow mic access for this site, or type to RC."
        );
        setListening(false);
        return;
      }
      wantListeningRef.current = false;
      setError(`Speech error: ${event.error}`);
      setListening(false);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setInterim("");
      if (ttsMutedRef.current) {
        // Stay in "listening" mode visually; resume after TTS.
        return;
      }
      if (!wantListeningRef.current) {
        setListening(false);
        return;
      }
      window.setTimeout(() => {
        if (!wantListeningRef.current || ttsMutedRef.current) return;
        try {
          beginRecognitionRef.current();
        } catch {
          wantListeningRef.current = false;
          setListening(false);
        }
      }, 150);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch (e) {
      if (wantListeningRef.current && !ttsMutedRef.current) {
        window.setTimeout(() => {
          if (wantListeningRef.current && !ttsMutedRef.current) {
            beginRecognitionRef.current();
          }
        }, 250);
      } else if (!wantListeningRef.current) {
        setError(e instanceof Error ? e.message : "Failed to start microphone");
        setListening(false);
      }
    }
  }, [abortRecognitionSession]);

  beginRecognitionRef.current = beginRecognition;

  /** Pause mic while RC talks; resume afterward without toggling listen mode off. */
  const setTtsMuted = useCallback(
    (muted: boolean) => {
      ttsMutedRef.current = muted;
      if (muted) {
        abortRecognitionSession();
        setInterim("");
        return;
      }
      ignoreUntilRef.current = Date.now() + 600;
      if (wantListeningRef.current) {
        beginRecognition();
      }
    },
    [abortRecognitionSession, beginRecognition]
  );

  const start = useCallback(() => {
    setError(null);
    if (!getSpeechRecognitionCtor()) {
      setError(
        "Speech recognition is not supported in this browser. Use Chrome or type instead."
      );
      return;
    }
    wantListeningRef.current = true;
    ttsMutedRef.current = false;
    beginRecognition();
  }, [beginRecognition]);

  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      ttsMutedRef.current = false;
      abortRecognitionSession();
    };
  }, [abortRecognitionSession]);

  return {
    listening,
    interim,
    error,
    setError,
    start,
    stop,
    setTtsMuted,
    supported: isSpeechRecognitionSupported(),
  };
}

let speakGeneration = 0;
let currentAudio: HTMLAudioElement | null = null;
let currentObjectUrl: string | null = null;

/** Clear handlers first so clearing src does not fire onerror → fake fallback. */
function disposeCurrentAudio(): void {
  const audio = currentAudio;
  currentAudio = null;
  if (audio) {
    audio.onended = null;
    audio.onerror = null;
    try {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    } catch {
      /* ignore */
    }
  }
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
    currentObjectUrl = null;
  }
}

function stopBrowserSpeech(): void {
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
}

/**
 * OpenAI southern-farmer TTS only (no browser robot voice).
 * Resolves when playback finishes or fails.
 */
export function speakText(text: string): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed) return Promise.resolve();

  const gen = ++speakGeneration;
  disposeCurrentAudio();
  stopBrowserSpeech();

  return (async () => {
    try {
      const blob = await rcSpeak(trimmed.slice(0, 2000));
      if (gen !== speakGeneration) return;

      // Error bodies can arrive as blobs with application/json when status slips through.
      if (
        blob.type.includes("json") ||
        blob.type.includes("text") ||
        blob.type.includes("html")
      ) {
        return;
      }

      const url = URL.createObjectURL(blob);
      currentObjectUrl = url;
      const audio = new Audio(url);
      currentAudio = audio;

      await new Promise<void>((resolve) => {
        audio.onended = () => {
          if (gen === speakGeneration) disposeCurrentAudio();
          resolve();
        };
        audio.onerror = () => {
          if (gen === speakGeneration) disposeCurrentAudio();
          resolve();
        };
        void audio.play().catch(() => {
          if (gen === speakGeneration) disposeCurrentAudio();
          resolve();
        });
      });
    } catch {
      /* stay silent rather than robot fallback */
    }
  })();
}

export function stopSpeaking(): void {
  speakGeneration += 1;
  disposeCurrentAudio();
  stopBrowserSpeech();
}
